// Owner: shell-nav (plan §9.2). Recent items are written by the shell when a route opens a record, a
// page, a deck, a document template, a dashboard or a saved view (`POST /nav/recent`), so no screen has
// to remember to do it, and the rail's Recent section and the empty ⌘K stay server-side and shared
// between devices.
import { useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { trackRecentContract } from '@dataroom/contracts/nav';
import type { NavItemRef } from '@dataroom/core';
import { api } from '../lib/api';
import { qk } from '../lib/queries';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The item a location opens (null for lists, settings and everything else). */
export function recentRefOf(pathname: string, search: Record<string, unknown>): NavItemRef | null {
  const p = pathname.replace(/\/+$/, '');
  let m = /^\/o\/([^/]+)\/r\/([^/]+)$/.exec(p);
  if (m && UUID.test(decodeURIComponent(m[2]!)))
    return { kind: 'record', objectKey: decodeURIComponent(m[1]!), id: decodeURIComponent(m[2]!) };
  m = /^\/o\/([^/]+)$/.exec(p);
  if (m && typeof search.view === 'string' && search.view)
    return { kind: 'view', objectKey: decodeURIComponent(m[1]!), id: search.view };
  m = /^\/(pages|decks|documents)\/([^/]+)$/.exec(p);
  if (m && UUID.test(decodeURIComponent(m[2]!))) {
    const kind = m[1] === 'decks' ? 'deck' : m[1] === 'documents' ? 'document' : 'page';
    return { kind, id: decodeURIComponent(m[2]!) };
  }
  m = /^\/dashboards\/([^/]+)$/.exec(p);
  if (m) return { kind: 'dashboard', id: decodeURIComponent(m[1]!) };
  return null;
}

const keyOf = (r: NavItemRef) => `${r.kind}:${r.objectKey ?? ''}:${r.id}`;
let last = '';

/** Remember the item the current route opens (once per item and visit). */
export function useTrackRecent(): void {
  const qc = useQueryClient();
  const location = useRouterState({ select: (s) => s.location });
  const ref = recentRefOf(location.pathname, location.search);
  const key = ref ? keyOf(ref) : '';
  useEffect(() => {
    if (!ref) {
      last = '';
      return;
    }
    if (key === last) return;
    // Wait a moment: a member who only passes through (keyboard j/k through records) is not tracked.
    const timer = window.setTimeout(() => {
      last = key;
      api.call(trackRecentContract, { body: ref }).then(
        () => void qc.invalidateQueries({ queryKey: qk.recent }),
        () => {
          // Not readable (deleted, no access): nothing to remember.
        },
      );
    }, 600);
    return () => window.clearTimeout(timer);
    // `ref` is derived from `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, qc]);
}
